import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { supabase, Tables } from "../utils/supabase";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  ClipboardDocumentListIcon,
  ChatBubbleLeftRightIcon,
  ExclamationTriangleIcon,
  BeakerIcon,
} from "@heroicons/react/24/outline";

interface SearchResult {
  id: string;
  type: "task" | "communication" | "complaint" | "preparation";
  title: string;
  subtitle: string;
  page: string;
}

interface GlobalSearchProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (page: string) => void;
}

const TYPE_ICONS = {
  task: ClipboardDocumentListIcon,
  communication: ChatBubbleLeftRightIcon,
  complaint: ExclamationTriangleIcon,
  preparation: BeakerIcon,
};

const TYPE_LABELS = {
  task: "Taak",
  communication: "Communicatie",
  complaint: "Klacht",
  preparation: "Bereiding",
};

const GlobalSearch: React.FC<GlobalSearchProps> = ({
  isOpen,
  onClose,
  onNavigate,
}) => {
  const { selectedPharmacy } = useAuth();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setResults([]);
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const search = useCallback(
    async (q: string) => {
      if (!q.trim() || !selectedPharmacy) {
        setResults([]);
        return;
      }
      setLoading(true);
      try {
        const [tasksRes, commsRes, complaintsRes, prepsRes] = await Promise.all(
          [
            supabase
              .from(Tables.TASKS)
              .select("id, title, status")
              .eq("pharmacy_id", selectedPharmacy.id)
              .ilike("title", `%${q}%`)
              .limit(4),
            supabase
              .from(Tables.COMMUNICATIONS)
              .select("id, title")
              .eq("pharmacy_id", selectedPharmacy.id)
              .ilike("title", `%${q}%`)
              .limit(4),
            supabase
              .from(Tables.COMPLAINTS)
              .select("id, complaint_number, description")
              .eq("pharmacy_id", selectedPharmacy.id)
              .or(
                `complaint_number.ilike.%${q}%,description.ilike.%${q}%`,
              )
              .limit(4),
            supabase
              .from(Tables.PREPARATIONS)
              .select("id, internal_lot_number, patient_name, preparation_form")
              .eq("pharmacy_id", selectedPharmacy.id)
              .or(
                `internal_lot_number.ilike.%${q}%,patient_name.ilike.%${q}%`,
              )
              .limit(4),
          ],
        );

        const all: SearchResult[] = [
          ...(tasksRes.data || []).map((t) => ({
            id: t.id,
            type: "task" as const,
            title: t.title,
            subtitle: `Taak · ${t.status === "pending" ? "Openstaand" : t.status === "in_progress" ? "Bezig" : t.status === "completed" ? "Voltooid" : t.status}`,
            page: "tasks",
          })),
          ...(commsRes.data || []).map((c) => ({
            id: c.id,
            type: "communication" as const,
            title: c.title,
            subtitle: "Communicatie",
            page: "communications",
          })),
          ...(complaintsRes.data || []).map((c) => ({
            id: c.id,
            type: "complaint" as const,
            title: c.complaint_number,
            subtitle: c.description.slice(0, 70),
            page: "complaints",
          })),
          ...(prepsRes.data || []).map((p) => ({
            id: p.id,
            type: "preparation" as const,
            title: p.internal_lot_number,
            subtitle: `${p.preparation_form}${p.patient_name ? ` · ${p.patient_name}` : ""}`,
            page: "compounding-preparations",
          })),
        ];

        setResults(all);
        setSelectedIndex(0);
      } catch {
        // silently fail
      } finally {
        setLoading(false);
      }
    },
    [selectedPharmacy],
  );

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(query), 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, search]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && results[selectedIndex]) {
      onNavigate(results[selectedIndex].page);
      onClose();
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 p-4 sm:p-6 md:p-20">
      <div
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        onClick={onClose}
      />
      <div className="relative mx-auto max-w-xl bg-white rounded-xl shadow-2xl overflow-hidden">
        {/* Input */}
        <div className="flex items-center px-4 py-3 border-b border-gray-200">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            className="ml-3 flex-1 bg-transparent text-sm text-gray-900 placeholder-gray-400 focus:outline-none"
            placeholder="Zoeken in taken, communicatie, klachten, bereidingen…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          {query ? (
            <button
              onClick={() => setQuery("")}
              className="ml-2 text-gray-400 hover:text-gray-500"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          ) : (
            <kbd className="ml-2 hidden sm:inline-flex items-center px-1.5 py-0.5 text-xs text-gray-400 bg-gray-100 rounded border border-gray-200">
              Esc
            </kbd>
          )}
        </div>

        {/* Body */}
        {loading && (
          <div className="px-4 py-8 text-center text-sm text-gray-500">
            Zoeken…
          </div>
        )}

        {!loading && query && results.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-gray-500">
            Geen resultaten voor &ldquo;{query}&rdquo;
          </div>
        )}

        {!loading && results.length > 0 && (
          <ul className="py-2 max-h-80 overflow-y-auto">
            {results.map((result, index) => {
              const Icon = TYPE_ICONS[result.type];
              return (
                <li key={`${result.type}-${result.id}`}>
                  <button
                    className={`w-full flex items-center px-4 py-3 text-left transition-colors ${
                      index === selectedIndex
                        ? "bg-primary-50"
                        : "hover:bg-gray-50"
                    }`}
                    onClick={() => {
                      onNavigate(result.page);
                      onClose();
                    }}
                    onMouseEnter={() => setSelectedIndex(index)}
                  >
                    <Icon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <div className="ml-3 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {result.title}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {TYPE_LABELS[result.type]} · {result.subtitle}
                      </p>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {!query && (
          <div className="px-4 py-6 text-center text-sm text-gray-400">
            Typ om te zoeken in alle modules
          </div>
        )}

        <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-t border-gray-100 text-xs text-gray-400">
          <span>
            <kbd className="px-1 py-0.5 bg-white border border-gray-200 rounded">
              ↑↓
            </kbd>{" "}
            navigeren &nbsp;
            <kbd className="px-1 py-0.5 bg-white border border-gray-200 rounded">
              ↵
            </kbd>{" "}
            openen
          </span>
          <span>
            <kbd className="px-1 py-0.5 bg-white border border-gray-200 rounded">
              Esc
            </kbd>{" "}
            sluiten
          </span>
        </div>
      </div>
    </div>
  );
};

export default GlobalSearch;
